<template>
  <div class="ci-bar">
    <!-- Mention dropdown (above bar) -->
    <div
      v-if="showMentionDropdown && filteredUsers.length"
      class="ci-bar__mentions"
    >
      <div
        v-for="user in filteredUsers"
        :key="user.id"
        class="ci-bar__mention-item"
        @mousedown.prevent="insertMention(user)"
      >
        <span class="ci-bar__mention-avatar">{{ getMentionInitials(user.fullName) }}</span>
        <div class="ci-bar__mention-info">
          <div class="ci-bar__mention-name">
            {{ user.fullName }}<template v-if="user.code"></template>
          </div>
          <div v-if="user.jobTitleName" class="ci-bar__mention-meta">
            {{ user.jobTitleName }}
          </div>
        </div>
      </div>
    </div>

    <!-- Wrap: previews + pill stacked vertically -->
    <div class="ci-bar__wrap">
      <!-- File previews (hiện trước khi gửi) -->
      <div v-if="pendingFiles.length" class="ci-bar__previews">
        <div
          v-for="(f, i) in pendingFiles"
          :key="i"
          class="ci-bar__preview-item"
        >
          <!-- Image thumbnail -->
          <template v-if="f.isImage">
            <div class="ci-bar__preview-img-wrap">
              <img :src="f.preview" :alt="f.name" class="ci-bar__preview-img" />
              <button class="ci-bar__preview-remove" @mousedown.prevent="removeFile(i)">
                <i class="fas fa-times" />
              </button>
            </div>
          </template>

          <!-- Non-image chip -->
          <template v-else>
            <div class="ci-bar__preview-file">
              <i :class="getPreviewFileIcon(f.name)" />
              <span class="ci-bar__preview-file-name">{{ f.name }}</span>
              <button class="ci-bar__preview-remove ci-bar__preview-remove--chip" @mousedown.prevent="removeFile(i)">
                <i class="fas fa-times" />
              </button>
            </div>
          </template>
        </div>
      </div>

      <!-- Input pill -->
      <div class="ci-bar__pill" :class="{ 'ci-bar__pill--focused': isFocused }">
        <!-- Avatar -->
        <div class="ci-bar__avatar">{{ authorInitial }}</div>

        <!-- Contenteditable editor -->
        <div
          ref="editor"
          class="ci-bar__editor"
          contenteditable="true"
          :data-placeholder="placeholder"
          @input="onInput"
          @keydown.enter.exact.prevent="submit"
          @keydown.esc="closeMentionDropdown"
          @focus="isFocused = true"
          @blur="isFocused = false"
        />

        <!-- Action buttons -->
        <div class="ci-bar__actions">
          <input
            ref="fileInput"
            type="file"
            multiple
            hidden
            @change="onFileChange"
          />
          <button
            type="button"
            class="ci-bar__action-btn"
            title="Đính kèm tệp"
            @click="$refs.fileInput.click()"
          >
            <i class="fas fa-paperclip" />
          </button>
          <button
            type="button"
            class="ci-bar__send-btn"
            :class="{ 'ci-bar__send-btn--active': canSubmit && !submitting }"
            :disabled="!canSubmit || submitting"
            :title="parentId !== null ? 'Trả lời' : 'Gửi'"
            @click="submit"
          >
            <b-spinner v-if="submitting" small />
            <i v-else class="fas fa-paper-plane" />
          </button>
        </div>
      </div>
    </div>

    <!-- Cancel reply -->
    <button
      v-if="parentId !== null"
      type="button"
      class="ci-bar__cancel-btn"
      title="Huỷ"
      @click="$emit('cancel')"
    >
      <i class="fas fa-times" />
    </button>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { typecheck } from '@/utils/common-upload-files';

export default {
  name: 'CommentInput',

  props: {
    module: { type: String, required: true },
    referenceId: { type: [String, Number], required: true },
    parentId: { type: Number, default: null },
    placeholder: { type: String, default: 'Nhập thảo luận ...' },
    initialMention: { type: Object, default: null },
  },

  data() {
    return {
      submitting: false,
      isFocused: false,
      editorEmpty: true,
      pendingFiles: [],
      mentionQuery: '',
      showMentionDropdown: false,
      allUsers: [],
      mentionStartNode: null,
      mentionStartOffset: -1,
    };
  },

  computed: {
    authorInitial() {
      const user = this.$store.getters.currentUser;
      return (user?.employee?.fullName || user?.username || 'U').charAt(0).toUpperCase();
    },
    filteredUsers() {
      if (!this.mentionQuery) return this.allUsers.slice(0, 8);
      const qRaw = this.mentionQuery.toLowerCase().trim();
      const q = this.xoaDau(qRaw);
      return this.allUsers
        .filter((u) => {
          const nameMatch = this.xoaDau((u.fullName || '').toLowerCase()).includes(q);
          const codeMatch = (u.code || '').toLowerCase().includes(qRaw);
          return nameMatch || codeMatch;
        })
        .slice(0, 8);
    },
    canSubmit() {
      return !this.editorEmpty || this.pendingFiles.length > 0;
    },
  },

  created() {
    this.fetchUsers();
  },

  mounted() {
    if (this.initialMention) {
      this.$nextTick(() => this.insertInitialMention(this.initialMention));
    }
  },

  methods: {
    insertInitialMention(user) {
      const editor = this.$refs.editor;
      if (!editor) return;

      const chip = document.createElement('span');
      chip.className = 'ci-mention-chip';
      chip.contentEditable = 'false';
      chip.dataset.mentionId = String(user.id);
      chip.dataset.mentionName = user.fullName;
      chip.textContent = `@${user.fullName}`;

      editor.appendChild(chip);
      const space = document.createTextNode(' ');
      editor.appendChild(space);

      const range = document.createRange();
      range.setStart(space, 1);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      editor.focus();
      this.editorEmpty = false;
    },

    fetchUsers() {
      ApiService.get('/user/for-mention').then(({ data }) => {
        this.allUsers =  data.data;
      });
    },

    onInput() {
      const editor = this.$refs.editor;
      this.editorEmpty = !editor || !editor.textContent.trim();

      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) { this.closeMentionDropdown(); return; }

      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      // Chỉ xử lý khi cursor đang trong text node
      if (!node || node.nodeType !== 3) { this.closeMentionDropdown(); return; }

      const textBefore = node.textContent.slice(0, range.startOffset);
      const match = textBefore.match(/@([^@]*)$/);
      if (match) {
        this.mentionStartNode = node;
        this.mentionStartOffset = textBefore.lastIndexOf('@');
        this.mentionQuery = match[1];
        this.showMentionDropdown = true;
      } else {
        this.closeMentionDropdown();
      }
    },

    insertMention(user) {
      const editor = this.$refs.editor;
      editor.focus();

      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || !this.mentionStartNode) {
        this.closeMentionDropdown();
        return;
      }

      // Xoá đoạn @query đang gõ
      const deleteRange = document.createRange();
      deleteRange.setStart(this.mentionStartNode, this.mentionStartOffset);
      deleteRange.setEnd(sel.getRangeAt(0).startContainer, sel.getRangeAt(0).startOffset);
      deleteRange.deleteContents();

      // Tạo chip mention
      const chip = document.createElement('span');
      chip.className = 'ci-mention-chip';
      chip.contentEditable = 'false';
      chip.dataset.mentionId = String(user.id);
      chip.dataset.mentionName = user.fullName;
      chip.textContent = `@${user.fullName}`;

      // Chèn chip tại vị trí cursor hiện tại
      const insertRange = sel.getRangeAt(0);
      insertRange.insertNode(chip);

      // Thêm khoảng trắng sau chip và đặt cursor vào đó
      const space = document.createTextNode(' ');
      chip.after(space);
      const newRange = document.createRange();
      newRange.setStart(space, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);

      this.closeMentionDropdown();
      this.editorEmpty = false;
    },

    closeMentionDropdown() {
      this.showMentionDropdown = false;
      this.mentionQuery = '';
      this.mentionStartNode = null;
      this.mentionStartOffset = -1;
    },

    // Đọc nội dung editor, chuyển chip → @[Name](id)
    getContent() {
      const editor = this.$refs.editor;
      if (!editor) return '';
      let content = '';
      const walk = (nodes) => {
        nodes.forEach((node) => {
          if (node.nodeType === 3) {
            content += node.textContent.replace(/\u00A0/g, ' ');
          } else if (node.nodeName === 'SPAN' && node.dataset.mentionId) {
            content += `@[${node.dataset.mentionName}](${node.dataset.mentionId})`;
          } else if (node.nodeName === 'BR') {
            content += '\n';
          } else {
            walk(Array.from(node.childNodes));
          }
        });
      };
      walk(Array.from(editor.childNodes));
      return content.trim();
    },

    clearEditor() {
      if (this.$refs.editor) this.$refs.editor.innerHTML = '';
      this.editorEmpty = true;
    },

    getPreviewFileIcon(fileName) {
      const ext = (fileName || '').split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'fas fa-file-pdf text-danger';
      if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word text-primary';
      if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel text-success';
      if (['mp4', 'mov', 'avi'].includes(ext)) return 'fas fa-file-video text-info';
      return 'fas fa-file-alt text-secondary';
    },

    onFileChange(e) {
      const files = Array.from(e.target.files || []);
      files.forEach((file) => {
        const isImage = typecheck(file.name);
        this.pendingFiles.push({
          file,
          name: file.name,
          isImage,
          preview: isImage ? URL.createObjectURL(file) : null,
        });
      });
      e.target.value = '';
    },

    removeFile(index) {
      const f = this.pendingFiles[index];
      if (f?.preview) URL.revokeObjectURL(f.preview);
      this.pendingFiles.splice(index, 1);
    },

    async uploadFiles() {
      const form = new FormData();
      this.pendingFiles.forEach(({ file }) => form.append('fileUploads', file));
      const { data } = await ApiService.post('file/upload-temp', form);
      return (data.data || []).map((f) => f.id).filter(Boolean);
    },

    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      try {
        let fileIds = [];
        if (this.pendingFiles.length) {
          fileIds = await this.uploadFiles();
        }
        const { data } = await ApiService.post('comments', {
          module: this.module,
          referenceId: String(this.referenceId),
          parentId: this.parentId,
          content: this.getContent(),
          fileIds,
        });
        this.clearEditor();
        this.pendingFiles.forEach(({ preview }) => { if (preview) URL.revokeObjectURL(preview); });
        this.pendingFiles = [];
        this.closeMentionDropdown();
        this.$emit('submitted', data.data);
      } catch (err) {
        this.$emit('error', err);
      } finally {
        this.submitting = false;
      }
    },

    getMentionInitials(fullName) {
      const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '?';
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    },

    xoaDau(str) {
      return str
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D');
    },
  },
};
</script>

<style src="./styles/comment-input.css" scoped></style>
<style src="./styles/comment-shared.css"></style>
